function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from "react";
import { FollowStatus, ItemMemberLevel, TeamMemberLevel, useTopicButton_RemoveMemberFromTopicMutation, ItemType } from "sprout-graphql";
import { Button } from "sprout-ui-dom/dist/Button";
import { useSnackbar } from "sprout-ui-dom/dist/Snackbar";
import { media } from "sprout-ui-theme";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import useToggle from "@sprout/hooks/dist/useToggle";
import { Link } from "react-router-dom";
import { EditOutlined } from "sprout-ui-icons";
import { getItemUrl } from "sprout-lib";
import styled, { css } from "styled-components";
import ActionConfirmationDialog from "../Dialog/ActionConfirmationDialog";
import useToggleRequestToJoinTopic from "./graphql/mutations/useToggleRequestToJoinTopic";
import useAcceptTopicInvite from "./graphql/mutations/useAcceptTopicInvite";
import useSetTopicFollow from "./graphql/mutations/useSetTopicFollow";
const FollowButton = styled(Button).withConfig({
    shouldForwardProp: (prop)=>![
            "showFallback"
        ].includes(prop)
})`
  ${({ showFallback  })=>showFallback && css`
      ${media.target("touch")} {
        display: none;
      }
    `}
`;
const MemberButton = styled(Button)`
  margin-left: 16px;
`;
const TopicButton = (props)=>{
    const currentUser = useCurrentUser();
    const [open, setOpen, setClose] = useToggle();
    const showSnackbar = useSnackbar();
    const { rootTopicOrSelf: rootTopic , showFallback  } = props;
    const { id , nodeId , currentUserFollowStatus , memberLevel , teamMembersCanJoin , usersCanAskToJoin , teamMembersJoinAs , owner , title ,  } = rootTopic;
    const rootURL = getItemUrl(rootTopic, owner);
    const amFollowing = currentUserFollowStatus && [
        FollowStatus.Following,
        FollowStatus.Subscribed
    ].includes(currentUserFollowStatus) || (owner === null || owner === void 0 ? void 0 : owner.amFollowing) && currentUserFollowStatus !== FollowStatus.Ignoring;
    // mutation - follow
    const handleFollow = useSetTopicFollow({
        amFollowing: !!amFollowing,
        nodeId,
        title,
        topicId: id
    });
    // mutation - join
    const handleJoin = useAcceptTopicInvite({
        nodeId,
        topicId: id,
        memberLevel,
        teamMembersJoinAs,
        userId: Number(currentUser === null || currentUser === void 0 ? void 0 : currentUser.id)
    });
    // mutation - request
    const handleRequest = useToggleRequestToJoinTopic({
        memberLevel,
        topicId: id,
        nodeId,
        userId: Number(currentUser === null || currentUser === void 0 ? void 0 : currentUser.id)
    });
    let render = null;
    // mutation - leave
    const [leaveTopic] = useTopicButton_RemoveMemberFromTopicMutation({
        variables: {
            topicId: id,
            userId: Number(currentUser === null || currentUser === void 0 ? void 0 : currentUser.id)
        },
        optimisticResponse: {
            removeMemberFromTopic: {
                itemByTopicId: {
                    nodeId,
                    id,
                    type: ItemType.Topic,
                    memberLevel: null,
                    __typename: "Item"
                },
                __typename: "RemoveMemberFromTopicPayload"
            }
        }
    });
    const handleLeave = useCallback(_asyncToGenerator(function*() {
        if (!open) {
            setOpen();
            return;
        }
        try {
            yield leaveTopic();
            showSnackbar({
                message: "You have left the tree"
            });
        } catch (e) {
            showSnackbar.error();
        }
    }), [
        leaveTopic,
        open,
        setOpen,
        showSnackbar
    ]);
    /**
   * 1. Users who are invited to the tree can join
   * 2. Users who are part of the workspace, and have not joined the tree can join,
   *    if and only if the tree's `teamMembersCanJoin` is `true`.
   */ if (memberLevel === ItemMemberLevel.Invited || (owner === null || owner === void 0 ? void 0 : owner.memberLevel) && teamMembersCanJoin && [
        TeamMemberLevel.Admin,
        TeamMemberLevel.Owner,
        TeamMemberLevel.Member, 
    ].includes(owner === null || owner === void 0 ? void 0 : owner.memberLevel) && !memberLevel) render = /*#__PURE__*/ _jsx(Button, {
        variant: "bright-blue",
        onClick: handleJoin,
        children: "Join"
    });
    // Request/Ask
    if (!render && usersCanAskToJoin && (memberLevel === null || memberLevel === ItemMemberLevel.Pending) && !!currentUser) render = /*#__PURE__*/ _jsx(Button, {
        variant: memberLevel === ItemMemberLevel.Pending ? "bright-teal" : "bright-blue",
        onClick: handleRequest,
        children: memberLevel === ItemMemberLevel.Pending ? "Requested" : "Ask To Join"
    });
    // Follow
    if (!render && !(memberLevel && [
        ItemMemberLevel.Owner,
        ItemMemberLevel.Admin,
        ItemMemberLevel.Contributor, 
    ].includes(memberLevel))) render = /*#__PURE__*/ _jsx(FollowButton, {
        showFallback: !!showFallback,
        variant: amFollowing ? "bright-teal" : "bright-blue",
        onClick: handleFollow,
        children: amFollowing ? "Following" : "Follow"
    });
    if (!render && showFallback && memberLevel && [
        ItemMemberLevel.Owner,
        ItemMemberLevel.Admin
    ].includes(memberLevel)) render = /*#__PURE__*/ _jsx(Button, {
        variant: "circle",
        as: Link,
        to: `${rootURL}/settings`,
        className: "Topic-Button",
        children: /*#__PURE__*/ _jsx(EditOutlined, {})
    });
    // Leave
    if (!render && showFallback && memberLevel && [
        ItemMemberLevel.Admin,
        ItemMemberLevel.Contributor
    ].includes(memberLevel)) render = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(MemberButton, {
                className: "hideOnHover",
                variant: "bright-teal",
                onClick: handleLeave,
                children: "Member"
            }),
            /*#__PURE__*/ _jsx(ActionConfirmationDialog, {
                title: "Leave tree",
                content: "When you leave a tree, your content will remain unless you delete it before leaving.",
                actionText: "Leave",
                onActionClick: handleLeave,
                open: open,
                onRequestClose: setClose
            })
        ]
    });
    return render;
};
export default TopicButton;
